import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { attachVatReimbursementRequest } from 'redux/reducers/swedBank/reducer';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { getVatReimbursementsRequest } from 'redux/reducers/vatReimbursement/reducer';
import { EasTable } from 'ui/EasTable';
import { TVatReimbursementItem } from 'redux/reducers/vatReimbursement/types';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import {
	getVatReimbursements,
	getVatReimbursementsLoading,
} from 'redux/reducers/vatReimbursement/selectors';
import { TAttachVatReimbursementDialogProps } from './types';
import { vatReimbursementStructure } from './vat-reimbursement-structure';
import { CountryWithFlag } from '../../../ui/CountryWithFlag';

export const AttachVatReimbursementDialog: FC<TAttachVatReimbursementDialogProps> = ({
	open,
	setClose,
	data,
	swedBankIntegrationRequest,
}) => {
	const dispatch = useDispatch();

	const items = useSelector(getVatReimbursements);
	const itemsIsLoading = useSelector(getVatReimbursementsLoading);

	const [selectedVatReimbursement, setSelectedVatReimbursement] =
		useState<TVatReimbursementItem | null>(null);

	useEffect(() => {
		if (!open) return;
		if (!data) return;
		const filters = [
			{
				fieldName: 'iossNumber',
				operator: 'like',
				value: data.iossReimbursementNumber,
			},
			{
				fieldName: 'moneyReceived',
				operator: '=',
				value: 'false',
			},
		];
		if (data.debitorCountry) {
			filters.push({
				fieldName: 'countryCode',
				operator: '=',
				value: data.debitorCountry,
			});
		}
		const orderBy = [
			{
				fieldName: 'reimbursementAmount',
				direction: ESortDirection.ASC,
			},
		];
		dispatch(
			getVatReimbursementsRequest({
				pageNumber: 1,
				pageSize: 100,
				filters,
				orderBy,
			}),
		);
	}, [data, open, dispatch]);

	const reset = () => {
		setSelectedVatReimbursement(null);
	};

	const handleClickAttachVatReimbursement = () => {
		console.log('handleClickAttachVatReimbursement', selectedVatReimbursement);
		if (selectedVatReimbursement === null) return;
		// dispatch(
		// 	markAsProcessedRequest({
		// 		body: {
		// 			incomingPaymentId: selectedVatReimbursement?.id,
		// 			type: 'attached',
		// 			invoiceNumber: null,
		// 		},
		// 		swedBankIntegrationRequest,
		// 	}),
		// );
		// dispatch(
		// 	updateCommentRequest({
		// 		id: selectedVatReimbursement?.id,
		// 		body: {
		// 			text: 'VAT Reimbursement attached',
		// 		},
		// 	}),
		// );
		if (!data?.id) return;
		if (!selectedVatReimbursement?.id) return;
		dispatch(
			attachVatReimbursementRequest({
				body: {
					vatReimbursementId: selectedVatReimbursement.id,
					incomingPaymentId: data.id,
				},
				swedBankIntegrationRequest,
			}),
		);
		reset();
		setClose();
	};

	if (!data) return null;

	return (
		<Dialog
			open={open}
			// onClose={setClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			fullWidth
			maxWidth="md"
		>
			<DialogTitle
				color="primary"
				sx={{
					padding: '10px 20px 0 20px',
				}}
			>
				Attach VAT Reimbursement
			</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						minWidth: '800px',
						maxWidth: '1500px',
						width: '100%',
						padding: '10px 0 0 0',
					}}
				>
					<Box
						sx={{
							// border: '1px solid #016193',
							padding: 1,
							margin: '0 0 10px 0',
							borderRadius: '12px',
							backgroundColor: 'primary.light',
						}}
					>
						<TableContainer>
							<Table>
								<TableBody>
									<TableRow>
										<TableCell
											color="primary"
											sx={{
												fontSize: '0.7rem',
												color: 'primary.main',
												padding: '0 8px 8px 0',
												border: 0,
											}}
										>
											Amount:
										</TableCell>
										<TableCell
											sx={{
												fontSize: '0.8rem',
												fontWeight: 'bold',
												color: 'primary.main',
												padding: '0 0 8px 0',
												border: 0,
											}}
										>
											<AmountAndCurrency
												amount={data.amount}
												currency={data.currency}
												size={0.8}
												bold="bold"
												color="primary"
											/>
										</TableCell>
									</TableRow>

									<TableRow>
										<TableCell
											color="primary"
											sx={{
												fontSize: '0.7rem',
												color: 'primary.main',
												padding: '0 8px 8px 0',
												border: 0,
												verticalAlign: 'top',
											}}
										>
											Reg Num:
										</TableCell>
										<TableCell
											sx={{
												fontSize: '0.8rem',
												fontWeight: 'bold',
												color: 'primary.main',
												padding: '0 0 8px 0',
												border: 0,
											}}
										>
											{data.endToEndId}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											color="primary"
											sx={{
												fontSize: '0.7rem',
												color: 'primary.main',
												padding: '0 8px 8px 0',
												border: 0,
											}}
										>
											IOSS Number:
										</TableCell>
										<TableCell
											sx={{
												fontSize: '0.8rem',
												fontWeight: 'bold',
												color: 'primary.main',
												padding: '0 0 8px 0',
												border: 0,
											}}
										>
											{data.iossReimbursementNumber}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											color="primary"
											sx={{
												fontSize: '0.7rem',
												color: 'primary.main',
												padding: '0 8px 8px 0',
												border: 0,
												verticalAlign: 'top',
											}}
										>
											Description:
										</TableCell>
										<TableCell
											sx={{
												fontSize: '0.8rem',
												fontWeight: 'bold',
												color: 'primary.main',
												padding: '0 0 8px 0',
												border: 0,
											}}
										>
											{data.paymentInformation}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											color="primary"
											sx={{
												fontSize: '0.7rem',
												color: 'primary.main',
												padding: '0 0 8px 0',
												border: 0,
											}}
										>
											Client Country:
										</TableCell>
										<TableCell
											sx={{
												fontSize: '0.8rem',
												fontWeight: 'bold',
												color: 'primary.main',
												padding: '0 0 8px 0',
												border: 0,
											}}
										>
											<CountryWithFlag
												countryCode={data.debitorCountry}
												size="m"
												bold
												color="primary.main"
											/>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
					<EasTable<TVatReimbursementItem>
						tableStructure={
							vatReimbursementStructure as TTableStructure<keyof TVatReimbursementItem>[]
						}
						items={items?.data ?? []}
						itemsIsLoading={itemsIsLoading}
						handleAction={() => {}}
						handleLink={(id: number) => {
							const item = items?.data?.find((vatItem) => vatItem.id === id);
							if (item) {
								setSelectedVatReimbursement(item);
							}
						}}
						highlightedRow={
							items?.data.findIndex((item) => item.id === selectedVatReimbursement?.id) ?? null
						}
						skeletonRows={new Array(3).fill(null)}
					/>

					{/* <Autocomplete
						id="monthly-fee-input"
						options={emListFullNames ?? []}
						sx={{ width: '100%' }}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Search EM"
								variant="outlined"
								sx={{ bgcolor: 'white', borderRadius: '5px' }}
								// hiddenLabel={false}
								placeholder="Search EM"
								size="small"
							/>
						)}
						value={em}
						onChange={handleChangeEm}
					/>
					<TextField
						label="Search Invoice"
						variant="outlined"
						sx={{ bgcolor: 'white', borderRadius: '5px' }}
						// hiddenLabel={false}
						placeholder="Search Invoice"
						size="small"
						fullWidth
						onChange={handleInvoiceNumber}
						value={invoiceNumber}
					/> */}

					{/* <InvoicesTableTable
						selectedInvoiceNumber={selectedInvoiceNumber}
						setSelectedInvoiceNumber={setSelectedInvoiceNumber}
					/> */}
				</Box>
			</DialogContent>
			<DialogActions>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'right',
						padding: '10px',
						alignItems: 'end',
						flexDirection: 'row',
						gap: 0,
					}}
				>
					<Button
						sx={{
							textTransform: 'none',
						}}
						onClick={() => {
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>

					{/* <Button
						sx={{
							textTransform: 'none',
						}}
						onClick={handleClickConnect}
						autoFocus
						disabled={selectedInvoiceNumber === null}
					>
						Connect to paid Invoice
					</Button> */}
					{/* <Button
						sx={{
							textTransform: 'none',
						}}
						onClick={handleClickConnect}
						autoFocus
						disabled={selectedInvoiceNumber === null}
					>
						Add to paid invoice
					</Button> */}
					<Button
						sx={{
							textTransform: 'none',
						}}
						onClick={handleClickAttachVatReimbursement}
						autoFocus
						disabled={selectedVatReimbursement === null}
					>
						Attach VAT Reimbursement
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};
